/**
 * KPI card (title, value, trend, optional info tooltip).
 */
import * as React from 'react';
import { LucideIcon, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { StatTrend } from '@/types/dashboard.types';

/** Props for {@link KpiCard}. */
interface KpiCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  /** Title color (defaults to the icon color). */
  titleColor?: string;
  trend?: StatTrend;
  description?: string;
  /** KPI definition shown in a tooltip on icon hover. */
  tooltip?: string;
  comparisonLabel?: string;
  isFilter?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Inner layout props shared by clickable and static KPI cards. */
interface KpiCardContentProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  iconColor: string;
  iconBgColor?: string;
  titleColor?: string;
  trend?: StatTrend;
  description?: string;
  tooltip?: string;
  comparisonLabel?: string;
}

/** Keep tooltip clicks from triggering a parent card action. */
function handleTooltipClick(event: React.MouseEvent): void {
  event.stopPropagation();
}

/** Keep tooltip pointer-down from starting a parent drag. */
function handleTooltipPointerDown(event: React.PointerEvent): void {
  event.stopPropagation();
}

/** Signed delta next to the trend percentage when the API sent one. */
function renderTrendDifference(trend: StatTrend): React.ReactNode {
  if (trend.differenceValue === undefined) return null;
  return (
    <span className="ml-1">
      ({trend.isPositive ? '+' : ''}
      {trend.differenceValue})
    </span>
  );
}

/** Optional comparison-period label after the trend. */
function renderComparisonLabel(comparisonLabel?: string): React.ReactNode {
  if (!comparisonLabel) return null;
  return <span className="ml-1 text-xs opacity-80">{comparisonLabel}</span>;
}

/** Info icon that opens the KPI definition tooltip. */
function renderTooltipTrigger(tooltip?: string): React.ReactNode {
  if (!tooltip) return null;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="kpi-card-tooltip-trigger"
            onClick={handleTooltipClick}
            onPointerDown={handleTooltipPointerDown}
          >
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" variant="dark" className="kpi-card-tooltip-content">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Trend row (arrow, percent, optional delta and comparison label). */
function renderTrend(trend?: StatTrend, comparisonLabel?: string): React.ReactNode {
  if (!trend) return null;
  const TrendIcon = trend.isPositive ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'kpi-card-trend',
        trend.isPositive ? 'kpi-card-trend-positive' : 'kpi-card-trend-negative',
      )}
    >
      <TrendIcon className="h-3 w-3" />
      {trend.value}%{renderTrendDifference(trend)}
      {renderComparisonLabel(comparisonLabel)}
    </span>
  );
}

/** Description line under the value. */
function renderDescription(description?: string): React.ReactNode {
  if (!description) return null;
  return <p className="kpi-card-description">{description}</p>;
}

/** Inner KPI layout (shared by clickable and static cards). */
const KpiCardContent: React.FC<KpiCardContentProps> = ({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBgColor,
  titleColor,
  trend,
  description,
  tooltip,
  comparisonLabel,
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <p className={cn('kpi-card-title', titleColor)}>{title}</p>
        {renderTooltipTrigger(tooltip)}
      </div>
      <p className="kpi-card-value">{value}</p>
      {renderTrend(trend, comparisonLabel)}
      {renderDescription(description)}
    </div>
    <div className={cn('kpi-card-icon-container', iconBgColor)}>
      <Icon className={cn('h-6 w-6', iconColor)} />
    </div>
  </div>
);

/** KPI card with icon, value, and optional trend. */
export const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  icon,
  iconColor = 'text-[var(--color-2-nuance-1)]',
  iconBgColor,
  titleColor,
  trend,
  description,
  tooltip,
  comparisonLabel,
  isFilter = false,
  isActive = false,
  onClick,
  className,
}) => {
  const cardClassName = cn(
    'kpi-card animate-fade-in-up',
    onClick && 'kpi-card-clickable',
    isFilter && isActive && 'kpi-card-active',
    className,
  );

  const content = (
    <KpiCardContent
      title={title}
      value={value}
      icon={icon}
      iconColor={iconColor}
      iconBgColor={iconBgColor}
      titleColor={titleColor}
      trend={trend}
      description={description}
      tooltip={tooltip}
      comparisonLabel={comparisonLabel}
    />
  );

  if (onClick) {
    return (
      <Button variant="custom" className={cardClassName} onClick={onClick}>
        {content}
      </Button>
    );
  }

  return <div className={cardClassName}>{content}</div>;
};

KpiCard.displayName = 'KpiCard';
